'use client';

/**
 * Classroom tools.
 *
 * IMPORTANT SCOPE NOTE, stated plainly in the UI as well as here: TypeSwift has
 * no backend and no accounts, so "the class" means the pupil profiles saved in
 * *this browser on this device*. That is genuinely useful for the common case
 * — a shared classroom computer or a small group — and it is honest about what
 * it is not. Everything needed for a real multi-device roster (auth, sync,
 * a server) is deliberately out of scope rather than faked.
 *
 * The lesson-assignment feature writes to the active pupil's profile, so it
 * unlocks a lesson for that child regardless of their position in the course.
 */
import React, { useMemo, useState } from 'react';
import { Download, Plus, Users } from 'lucide-react';
import { useKids } from '../context/KidsProvider';
import { LEVELS, ALL_LESSONS, getLesson } from '../data/curriculum';
import { overview, toCsv } from '../lib/analytics';
import { completedCount, totalStars } from '../lib/progress';
import Avatar from './Avatar';
import { LoadingBlock, StatTile, ProgressRail, Pill } from './ui';

export default function TeacherPanel() {
  const { hydrated, store, profile, createProfile, switchProfile, setClassroom, assignLesson } =
    useKids();
  const [newName, setNewName] = useState('');
  const [levelId, setLevelId] = useState(LEVELS[0].id);

  const roster = useMemo(
    () =>
      store.profiles
        .map((p) => {
          const stats = overview(p);
          return {
            profile: p,
            lessons: completedCount(p),
            stars: totalStars(p),
            bestWpm: stats.bestWpm,
            accuracy: stats.recentAccuracy || 0,
            minutes: stats.totalMinutes,
            lastSeen: p.sessions[p.sessions.length - 1]?.day ?? '—',
            streak: p.streak.current,
          };
        })
        .sort((a, b) => b.lessons - a.lessons || b.bestWpm - a.bestWpm),
    [store.profiles]
  );

  if (!hydrated) return <LoadingBlock height={420} />;

  const exportCsv = () => {
    const csv = toCsv(
      roster.map((row) => ({
        Name: row.profile.name,
        'Lessons completed': row.lessons,
        'Total lessons': ALL_LESSONS.length,
        Stars: row.stars,
        'Best WPM': row.bestWpm,
        'Recent accuracy %': row.accuracy,
        'Practice minutes': row.minutes,
        'Current streak (days)': row.streak,
        'Last practised': row.lastSeen,
      }))
    );
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(store.classroom?.name || 'class').replace(/\W+/g, '-').toLowerCase()}-typing-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const classAverage = roster.length
    ? Math.round(roster.reduce((sum, r) => sum + r.bestWpm, 0) / roster.length)
    : 0;
  const classLessons = roster.reduce((sum, r) => sum + r.lessons, 0);
  const level = LEVELS.find((l) => l.id === levelId) ?? LEVELS[0];

  return (
    <div className="grid gap-6">
      {/* ── scope disclosure — never hide this ─────────────────────────── */}
      <div
        className="k-card p-4 flex gap-3"
        style={{ background: 'var(--k-sky-soft)', borderColor: 'var(--k-sky)' }}
      >
        <span style={{ fontSize: 22 }} aria-hidden="true">
          ℹ️
        </span>
        <p className="text-sm">
          <strong>This class lives on this device.</strong> TypeSwift has no accounts and no server,
          so the roster below is the set of pupil profiles saved in this browser. That works well
          for a shared classroom computer or a small group. It does not sync between devices — use{' '}
          <em>Export CSV</em> to move results into your own records.
        </p>
      </div>

      {/* ── class identity ─────────────────────────────────────────────── */}
      <section className="k-card p-5">
        <h2 className="text-base mb-3">Class details</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm font-bold">
            Class name
            <input
              defaultValue={store.classroom?.name ?? ''}
              placeholder="e.g. Year 4 Blue"
              onBlur={(e) =>
                setClassroom({ name: e.target.value, teacher: store.classroom?.teacher ?? '' })
              }
              className="w-full mt-1 px-3 py-2.5 rounded-xl font-bold"
              style={{
                background: 'var(--input)',
                border: '2px solid var(--border-strong)',
                color: 'var(--foreground)',
              }}
            />
          </label>
          <label className="text-sm font-bold">
            Teacher
            <input
              defaultValue={store.classroom?.teacher ?? ''}
              placeholder="e.g. Ms Okafor"
              onBlur={(e) =>
                setClassroom({ name: store.classroom?.name ?? '', teacher: e.target.value })
              }
              className="w-full mt-1 px-3 py-2.5 rounded-xl font-bold"
              style={{
                background: 'var(--input)',
                border: '2px solid var(--border-strong)',
                color: 'var(--foreground)',
              }}
            />
          </label>
        </div>
      </section>

      {/* ── class stats ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          label="Pupils"
          value={roster.length}
          sub="on this device"
          emoji="👥"
          color="--k-sky"
        />
        <StatTile
          label="Lessons done"
          value={classLessons}
          sub="across the class"
          emoji="📚"
          color="--k-grape"
        />
        <StatTile
          label="Average best"
          value={`${classAverage}`}
          sub="words per minute"
          emoji="⚡"
          color="--k-mint"
        />
        <StatTile
          label="Practising today"
          value={roster.filter((r) => r.streak > 0).length}
          sub="active streaks"
          emoji="🔥"
          color="--k-tangerine"
        />
      </div>

      {/* ── add pupil ─────────────────────────────────────────────────── */}
      <section className="k-card p-5">
        <h2 className="text-base mb-3">Add a pupil</h2>
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newName.trim()) return;
            createProfile(newName);
            setNewName('');
          }}
        >
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="First name only"
            maxLength={24}
            aria-label="New pupil first name"
            className="flex-1 min-w-[200px] px-3 py-2.5 rounded-xl font-bold"
            style={{
              background: 'var(--input)',
              border: '2px solid var(--border-strong)',
              color: 'var(--foreground)',
            }}
          />
          <button type="submit" className="k-btn k-btn-primary k-btn-sm" disabled={!newName.trim()}>
            <Plus size={16} aria-hidden="true" /> Add
          </button>
        </form>
        <p className="text-xs text-muted-foreground mt-2">
          First names only. Do not enter surnames, dates of birth or anything else identifying — the
          software does not need it and you should not store it in a shared browser.
        </p>
      </section>

      {/* ── roster / leaderboard ──────────────────────────────────────── */}
      <section className="k-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-base">
            <Users size={16} className="inline mr-1.5 -mt-0.5" aria-hidden="true" />
            Class progress
          </h2>
          <button
            type="button"
            onClick={exportCsv}
            className="k-btn k-btn-secondary k-btn-sm"
            disabled={roster.length === 0}
          >
            <Download size={15} aria-hidden="true" /> Export CSV
          </button>
        </div>

        {roster.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No pupils yet. Add one above to get started.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr>
                  {[
                    '#',
                    'Pupil',
                    'Progress',
                    'Stars',
                    'Best WPM',
                    'Accuracy',
                    'Minutes',
                    'Last seen',
                  ].map((head) => (
                    <th key={head} className="k-eyebrow text-left py-2 pr-3">
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {roster.map((row, i) => (
                  <tr key={row.profile.id} style={{ borderTop: '2px solid var(--border)' }}>
                    <td className="py-2.5 pr-3 font-extrabold tabular-nums">{i + 1}</td>
                    <td className="py-2.5 pr-3">
                      <button
                        type="button"
                        onClick={() => switchProfile(row.profile.id)}
                        className="flex items-center gap-2 font-extrabold hover:underline"
                      >
                        <Avatar config={row.profile.avatar} size={28} />
                        {row.profile.name}
                        {row.profile.id === profile?.id && (
                          <Pill color="--k-grape" className="!text-[0.6rem] !py-0.5">
                            active
                          </Pill>
                        )}
                      </button>
                    </td>
                    <td className="py-2.5 pr-3 min-w-[130px]">
                      <ProgressRail value={row.lessons} max={ALL_LESSONS.length} />
                      <span className="text-[0.68rem] font-bold text-muted-foreground tabular-nums">
                        {row.lessons}/{ALL_LESSONS.length}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 font-bold tabular-nums">⭐ {row.stars}</td>
                    <td className="py-2.5 pr-3 font-bold tabular-nums">{row.bestWpm}</td>
                    <td className="py-2.5 pr-3 font-bold tabular-nums">{row.accuracy || '—'}%</td>
                    <td className="py-2.5 pr-3 font-bold tabular-nums">{row.minutes}</td>
                    <td className="py-2.5 pr-3 text-muted-foreground tabular-nums">
                      {row.lastSeen}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── assignments ───────────────────────────────────────────────── */}
      <section className="k-card p-5">
        <h2 className="text-base mb-1">Assign lessons</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Assigning a lesson unlocks it for{' '}
          <strong className="text-foreground">{profile?.name ?? 'the active pupil'}</strong> even if
          they have not reached it yet, and pins it to the top of their next-up list. Switch pupil
          in the table above to assign to someone else.
        </p>

        <label className="text-sm font-bold block mb-3">
          Level
          <select
            value={levelId}
            onChange={(e) => setLevelId(e.target.value)}
            className="w-full sm:w-80 mt-1 px-3 py-2.5 rounded-xl font-bold"
            style={{
              background: 'var(--input)',
              border: '2px solid var(--border-strong)',
              color: 'var(--foreground)',
            }}
          >
            {LEVELS.map((l) => (
              <option key={l.id} value={l.id}>
                Level {l.index}: {l.title} — {l.subtitle}
              </option>
            ))}
          </select>
        </label>

        <ul className="grid gap-2">
          {level.lessons.map((lesson) => {
            const assigned = profile?.assigned.includes(lesson.id) ?? false;
            return (
              <li key={lesson.id} className="flex items-center gap-3">
                <input
                  id={`assign-${lesson.id}`}
                  type="checkbox"
                  checked={assigned}
                  onChange={(e) => assignLesson(lesson.id, e.target.checked)}
                  disabled={!profile}
                  style={{ width: 20, height: 20, accentColor: 'var(--accent)' }}
                />
                <label htmlFor={`assign-${lesson.id}`} className="text-sm font-bold cursor-pointer">
                  {lesson.title}
                  <span className="text-muted-foreground font-normal">
                    {' '}
                    · target {lesson.targetWpm} wpm / {lesson.targetAccuracy}%
                  </span>
                </label>
              </li>
            );
          })}
        </ul>

        {profile && profile.assigned.length > 0 && (
          <div className="mt-4 pt-4" style={{ borderTop: '2px solid var(--border)' }}>
            <p className="k-eyebrow mb-2">Currently assigned to {profile.name}</p>
            <div className="flex flex-wrap gap-2">
              {profile.assigned.map((id) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => assignLesson(id, false)}
                  className="k-pill"
                  style={{ background: 'var(--k-sky-soft)', color: 'var(--k-sky)' }}
                >
                  {getLesson(id)?.title ?? id} ✕
                </button>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
